/**
 * StatusEffectTracker — per-entity runtime manager for active status effects.
 *
 * Handles the full lifecycle: application (with stacking rules), per-frame
 * tick (cooldown/immunity decrement, DoT damage), natural expiry, and
 * manual removal. Enforces the CC hierarchy so that higher-severity
 * effects subsume lower ones, and CC immunity windows prevent permanent
 * crowd-control lock.
 *
 * One tracker instance per entity. Created by StatusEffectSystem.createTracker().
 *
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type { StatusEffectDef, StatusEffectSystem, StackingRule } from './StatusEffectSystem';
import { SimulationBridge } from '../simulation/SimulationBridge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** An active instance of a status effect, possibly from a specific source. */
export interface ActiveStatusEffect {
  readonly def: StatusEffectDef;
  readonly sourceSkillId: string;
  stacks: number;
  remainingMs: number;        // -1 = permanent (never decrements)
  tickTimer: number;
  intensity: number;
  /**
   * Optional resolved tick interval in ms. When set, overrides
   * `def.defaultTickIntervalMs` for both the initial timer and per-tick reset.
   * Used by Kindle (and other tick-acceleration passives) to apply tree-wide
   * DoT acceleration at application time.
   */
  resolvedTickIntervalMs?: number;
  /**
   * Cumulative wall-clock ms this effect has been active (non-expired).
   * Incremented each frame by delta. Used by Sear to detect when burning
   * has been active for 2+ seconds (sear threshold).
   */
  elapsedMs: number;
  /**
   * Optional threshold in ms. When `elapsedMs` first reaches this value,
   * the tracker emits a `dot_threshold` event. Used by Sear to trigger
   * armor-reduction application at the 2000ms burn threshold.
   */
  thresholdMs?: number;
  /** Set to true once the threshold event has been emitted (prevents re-firing). */
  thresholdFired: boolean;
}

/** Options for applying a status effect. */
export interface ApplyOptions {
  sourceSkillId: string;
  durationMs?: number;          // Overrides def default. -1 for permanent.
  intensity?: number;           // Overrides def default.
  stacks?: number;              // Stacks to add (intensity stacking). Default 1.
  stackingRuleOverride?: StackingRule;
  /**
   * Override the DoT tick interval in ms. When provided, stored on the
   * ActiveStatusEffect instance as `resolvedTickIntervalMs`, overriding
   * `def.defaultTickIntervalMs` for tick timing. Used by Kindle to apply
   * tick acceleration (500ms × 0.75 = 375ms) at application time.
   */
  tickIntervalMs?: number;
  /**
   * Optional elapsed-time threshold in ms. When provided, the tracker emits
   * a `dot_threshold` event the first time `elapsedMs` reaches this value.
   * Used by Sear: burning with `thresholdMs: 2000` fires the event when the
   * target has been burning for 2 continuous seconds.
   */
  thresholdMs?: number;
  maxIndependentInstances?: number; // Cap on concurrent independent instances (e.g. Saturation: 5)
}

/** Events emitted during update(). */
export interface StatusEffectEvent {
  type: 'dot_tick' | 'expired' | 'immunity_started' | 'dot_threshold';
  defId: string;
  sourceSkillId: string;
  damage?: number;              // For dot_tick events
  immunityMs?: number;          // For immunity_started events
  /** For dot_threshold events: the threshold value that was crossed. */
  thresholdMs?: number;
}

// ---------------------------------------------------------------------------
// CC Hierarchy
// ---------------------------------------------------------------------------

/**
 * CC severity hierarchy: stun > stagger > disruption > root > slow.
 * Index 0 is the highest severity.
 */
const CC_HIERARCHY = ['stun', 'stagger', 'disruption', 'root', 'slow'] as const;

function ccSeverity(defId: string): number {
  const idx = CC_HIERARCHY.indexOf(defId as typeof CC_HIERARCHY[number]);
  return idx === -1 ? Infinity : idx;
}

// ---------------------------------------------------------------------------
// Tracker
// ---------------------------------------------------------------------------

export class StatusEffectTracker {
  /**
   * Active effects keyed by defId. Each defId maps to an array of
   * ActiveStatusEffect instances — one per unique sourceSkillId for
   * source-tagged effects, or a single entry for non-tagged effects.
   */
  private readonly activeEffects = new Map<string, ActiveStatusEffect[]>();

  /** CC immunity timers: defId -> remaining immunity ms. */
  private readonly immunityTimers = new Map<string, number>();

  /** Flat immunity flag: blocks all new 'poison' applications to this entity. */
  private _selfPoisonImmunity = false;

  /**
   * Simulation tag — when set, apply/remove calls record status events
   * through the SimulationBridge. Set by the bridge during scenario setup.
   */
  entityTag: string | undefined;

  /**
   * CC bridge callback — fired when a CC-category effect is successfully
   * applied. Used to bridge tracker state into AI state (e.g., stagger
   * freezes the AI). Set by GameScene during enemy registration.
   */
  onCCApplied: ((defId: string, durationMs: number) => void) | null = null;

  /**
   * Guard flag — set true when CombatHitResolver is writing a melee stagger
   * to the tracker so the onCCApplied callback does not re-apply stagger
   * to the AI (which already has it from the melee path). Prevents an
   * infinite loop between the melee→tracker and tracker→AI bridges.
   */
  _bridgingFromMelee = false;

  constructor(private readonly system: StatusEffectSystem) {}

  /** Set or clear the self-poison immunity flag (granted by the Immunity passive). */
  setSelfPoisonImmunity(value: boolean): void {
    this._selfPoisonImmunity = value;
  }

  /** Whether this entity is immune to new poison applications. */
  get selfPoisonImmunity(): boolean {
    return this._selfPoisonImmunity;
  }

  // -----------------------------------------------------------------------
  // Apply
  // -----------------------------------------------------------------------

  /**
   * Apply a status effect to this entity.
   *
   * Returns the resulting ActiveStatusEffect, or null if the application
   * was rejected (CC immunity, CC subsumption, max stacks reached).
   */
  apply(defId: string, opts: ApplyOptions): ActiveStatusEffect | null {
    const def = this.system.getDef(defId);
    if (!def) return null;

    // Self-poison immunity (Immunity passive)
    if (defId === 'poison' && this._selfPoisonImmunity) return null;

    // CC immunity check
    if (def.category === 'cc' && this.isImmune(defId)) return null;

    // CC subsumption: stun blocks stagger application (the only blocking rule).
    // Disruption, root, and slow all coexist independently with every other CC.
    if (defId === 'stagger' && this.hasRaw('stun')) {
      return null;
    }

    const rule = opts.stackingRuleOverride ?? def.stackingRule;
    const duration = opts.durationMs ?? def.defaultDurationMs ?? 5000;
    const intensity = opts.intensity ?? def.defaultIntensity ?? 0;
    const stacksToAdd = opts.stacks ?? 1;

    let existing = this.activeEffects.get(defId);
    if (!existing) {
      existing = [];
      this.activeEffects.set(defId, existing);
    }

    const tickInterval = opts.tickIntervalMs;
    const thresholdMs = opts.thresholdMs;

    const applyAndRecord = (result: ActiveStatusEffect | null): ActiveStatusEffect | null => {
      if (result) {
        if (this.entityTag) {
          SimulationBridge.getInstance()?.recordStatus({
            timestamp: Date.now(),
            statusId: defId,
            targetTag: this.entityTag,
            applied: true,
          });
        }
        // Fire CC bridge callback for CC-category effects (skip when
        // the melee path already applied stagger to AI — prevents loop)
        if (def.category === 'cc' && this.onCCApplied && !this._bridgingFromMelee) {
          this.onCCApplied(defId, duration);
        }
      }
      return result;
    };

    switch (rule) {
      case 'refresh':
        return applyAndRecord(this.applyRefresh(existing, def, opts.sourceSkillId, duration, intensity, tickInterval, thresholdMs));

      case 'intensity':
        return applyAndRecord(this.applyIntensity(existing, def, opts.sourceSkillId, duration, intensity, stacksToAdd, tickInterval, thresholdMs));

      case 'duration':
        return applyAndRecord(this.applyDuration(existing, def, opts.sourceSkillId, duration, intensity, tickInterval, thresholdMs));

      case 'independent':
        return applyAndRecord(this.applyIndependent(existing, def, opts.sourceSkillId, duration, intensity, stacksToAdd, tickInterval, thresholdMs, opts.maxIndependentInstances));

      case 'override':
        return applyAndRecord(this.applyOverride(existing, def, opts.sourceSkillId, duration, intensity, tickInterval, thresholdMs));
    }
  }

  /** Refresh: reset duration on existing instance, or create one. */
  private applyRefresh(
    instances: ActiveStatusEffect[],
    def: StatusEffectDef,
    sourceSkillId: string,
    durationMs: number,
    intensity: number,
    tickIntervalMs?: number,
    thresholdMs?: number,
  ): ActiveStatusEffect {
    if (instances.length > 0) {
      const inst = instances[0];
      inst.remainingMs = durationMs;
      return inst;
    }
    const inst = this.createInstance(def, sourceSkillId, durationMs, intensity, 1, tickIntervalMs, thresholdMs);
    instances.push(inst);
    return inst;
  }

  /**
   * Intensity: stack within same source group.
   * Each source maintains its own instance. Total stacks capped at maxStacks.
   */
  private applyIntensity(
    instances: ActiveStatusEffect[],
    def: StatusEffectDef,
    sourceSkillId: string,
    durationMs: number,
    intensity: number,
    stacksToAdd: number,
    tickIntervalMs?: number,
    thresholdMs?: number,
  ): ActiveStatusEffect | null {
    const maxStacks = def.maxStacks ?? Infinity;
    const totalStacks = instances.reduce((sum, inst) => sum + inst.stacks, 0);

    if (totalStacks >= maxStacks) return null; // Cap reached

    // Find existing instance from same source
    const sameSource = instances.find((inst) => inst.sourceSkillId === sourceSkillId);
    if (sameSource) {
      const available = maxStacks - totalStacks;
      const added = Math.min(stacksToAdd, available);
      sameSource.stacks += added;
      sameSource.remainingMs = durationMs; // Duration refreshes
      sameSource.intensity = intensity;
      return sameSource;
    }

    // New source
    const available = maxStacks - totalStacks;
    const added = Math.min(stacksToAdd, available);
    const inst = this.createInstance(def, sourceSkillId, durationMs, intensity, added, tickIntervalMs, thresholdMs);
    instances.push(inst);
    return inst;
  }

  /** Duration: extend duration on existing instance, or create one. */
  private applyDuration(
    instances: ActiveStatusEffect[],
    def: StatusEffectDef,
    sourceSkillId: string,
    durationMs: number,
    intensity: number,
    tickIntervalMs?: number,
    thresholdMs?: number,
  ): ActiveStatusEffect {
    const sameSource = instances.find((inst) => inst.sourceSkillId === sourceSkillId);
    if (sameSource) {
      if (sameSource.remainingMs !== -1) {
        sameSource.remainingMs += durationMs;
      }
      return sameSource;
    }
    const inst = this.createInstance(def, sourceSkillId, durationMs, intensity, 1, tickIntervalMs, thresholdMs);
    instances.push(inst);
    return inst;
  }

  /** Independent: each application is a separate tracked instance. */
  private applyIndependent(
    instances: ActiveStatusEffect[],
    def: StatusEffectDef,
    sourceSkillId: string,
    durationMs: number,
    intensity: number,
    stacks: number,
    tickIntervalMs?: number,
    thresholdMs?: number,
    maxInstances?: number,
  ): ActiveStatusEffect | null {
    if (maxInstances !== undefined && instances.length >= maxInstances) return null;
    const inst = this.createInstance(def, sourceSkillId, durationMs, intensity, stacks, tickIntervalMs, thresholdMs);
    instances.push(inst);
    return inst;
  }

  /** Override: stronger replaces weaker. For CC, longer duration wins. */
  private applyOverride(
    instances: ActiveStatusEffect[],
    def: StatusEffectDef,
    sourceSkillId: string,
    durationMs: number,
    intensity: number,
    tickIntervalMs?: number,
    thresholdMs?: number,
  ): ActiveStatusEffect {
    if (instances.length > 0) {
      const current = instances[0];
      // For CC: longer remaining duration wins. For others: higher intensity wins.
      const shouldReplace = def.category === 'cc'
        ? durationMs > current.remainingMs
        : intensity > current.intensity;

      if (shouldReplace) {
        const replacement = this.createInstance(def, sourceSkillId, durationMs, intensity, 1, tickIntervalMs, thresholdMs);
        instances[0] = replacement;
        return replacement;
      }
      return current;
    }
    const inst = this.createInstance(def, sourceSkillId, durationMs, intensity, 1, tickIntervalMs, thresholdMs);
    instances.push(inst);
    return inst;
  }

  private createInstance(
    def: StatusEffectDef,
    sourceSkillId: string,
    durationMs: number,
    intensity: number,
    stacks: number,
    tickIntervalMs?: number,
    thresholdMs?: number,
  ): ActiveStatusEffect {
    const resolvedTick = tickIntervalMs ?? def.defaultTickIntervalMs ?? 0;
    return {
      def,
      sourceSkillId,
      stacks,
      remainingMs: durationMs,
      tickTimer: resolvedTick,
      intensity,
      resolvedTickIntervalMs: tickIntervalMs,
      elapsedMs: 0,
      thresholdMs,
      thresholdFired: false,
    };
  }

  // -----------------------------------------------------------------------
  // Removal
  // -----------------------------------------------------------------------

  /** Remove all instances of a status effect. Does NOT trigger immunity. */
  remove(defId: string): void {
    if (this.entityTag && this.activeEffects.has(defId)) {
      SimulationBridge.getInstance()?.recordStatus({
        timestamp: Date.now(),
        statusId: defId,
        targetTag: this.entityTag,
        applied: false,
      });
    }
    this.activeEffects.delete(defId);
  }

  /** Remove all status effects. Typically called on entity death. */
  removeAll(): void {
    this.activeEffects.clear();
    this.immunityTimers.clear();
  }

  // -----------------------------------------------------------------------
  // Queries
  // -----------------------------------------------------------------------

  /**
   * Check if entity has a specific effect.
   *
   * CC subsumption: has('stagger') returns true if stun is active,
   * because stun subsumes stagger in the CC hierarchy.
   */
  has(defId: string): boolean {
    if (this.hasRaw(defId)) return true;

    // Stun subsumes stagger
    if (defId === 'stagger' && this.hasRaw('stun')) return true;

    return false;
  }

  /** Direct check without CC subsumption logic. */
  private hasRaw(defId: string): boolean {
    const effects = this.activeEffects.get(defId);
    return effects !== undefined && effects.length > 0;
  }

  /** Get total stack count across all sources. Returns 0 if not present. */
  getStacks(defId: string): number {
    const effects = this.activeEffects.get(defId);
    if (!effects) return 0;
    return effects.reduce((sum, inst) => sum + inst.stacks, 0);
  }

  /** Get the first active instance (or undefined). */
  get(defId: string): ActiveStatusEffect | undefined {
    const effects = this.activeEffects.get(defId);
    return effects?.[0];
  }

  /** Get all active instances for a specific effect. */
  getInstances(defId: string): ReadonlyArray<ActiveStatusEffect> {
    return this.activeEffects.get(defId) ?? [];
  }

  /** Get all active effects across all types. */
  getAll(): ReadonlyArray<ActiveStatusEffect> {
    const all: ActiveStatusEffect[] = [];
    for (const instances of this.activeEffects.values()) {
      all.push(...instances);
    }
    return all;
  }

  /** Check if entity is in a CC immunity window for a given effect. */
  isImmune(defId: string): boolean {
    const timer = this.immunityTimers.get(defId);
    return timer !== undefined && timer > 0;
  }

  // -----------------------------------------------------------------------
  // Update
  // -----------------------------------------------------------------------

  /**
   * Tick all active effects. Called each frame.
   *
   * Returns events for DoT ticks and natural expiries. The caller
   * (CombatHitResolver, HealthComponent) processes these events to
   * apply damage, spawn particles, etc.
   */
  update(delta: number): StatusEffectEvent[] {
    const events: StatusEffectEvent[] = [];

    // Tick immunity timers
    for (const [defId, timer] of this.immunityTimers) {
      const remaining = timer - delta;
      if (remaining <= 0) {
        this.immunityTimers.delete(defId);
      } else {
        this.immunityTimers.set(defId, remaining);
      }
    }

    // Tick each active effect
    for (const [defId, instances] of this.activeEffects) {
      const expired: number[] = [];

      for (let i = 0; i < instances.length; i++) {
        const inst = instances[i];

        // Tick duration (skip permanent effects)
        if (inst.remainingMs !== -1) {
          inst.remainingMs -= delta;
          if (inst.remainingMs <= 0) {
            expired.push(i);
            events.push({
              type: 'expired',
              defId,
              sourceSkillId: inst.sourceSkillId,
            });

            // Start immunity window on natural expiry (CC effects only)
            if (inst.def.immunityAfterExpiryMs) {
              const existing = this.immunityTimers.get(defId) ?? 0;
              // Don't shorten an existing immunity window
              if (inst.def.immunityAfterExpiryMs > existing) {
                this.immunityTimers.set(defId, inst.def.immunityAfterExpiryMs);
                events.push({
                  type: 'immunity_started',
                  defId,
                  sourceSkillId: inst.sourceSkillId,
                  immunityMs: inst.def.immunityAfterExpiryMs,
                });
              }
            }
            continue;
          }
        }

        // Track elapsed wall time (only for non-expired effects)
        inst.elapsedMs += delta;

        // Emit dot_threshold event when elapsedMs crosses the configured threshold
        if (
          inst.thresholdMs !== undefined &&
          !inst.thresholdFired &&
          inst.elapsedMs >= inst.thresholdMs
        ) {
          inst.thresholdFired = true;
          events.push({
            type: 'dot_threshold',
            defId,
            sourceSkillId: inst.sourceSkillId,
            thresholdMs: inst.thresholdMs,
          });
        }

        // Tick DoT
        if (inst.def.category === 'dot' && inst.def.defaultTickIntervalMs) {
          const tickInterval = inst.resolvedTickIntervalMs ?? inst.def.defaultTickIntervalMs;
          inst.tickTimer -= delta;
          while (inst.tickTimer <= 0) {
            events.push({
              type: 'dot_tick',
              defId,
              sourceSkillId: inst.sourceSkillId,
              damage: inst.intensity * inst.stacks,
            });
            inst.tickTimer += tickInterval;
          }
        }
      }

      // Remove expired instances (reverse order to preserve indices)
      for (let i = expired.length - 1; i >= 0; i--) {
        instances.splice(expired[i], 1);
      }

      // Clean up empty arrays
      if (instances.length === 0) {
        this.activeEffects.delete(defId);
      }
    }

    return events;
  }
}
